'use strict';

import { observable, observe, configure, action, flow, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import termStore from "@uw-it-sis/lib-react/lib/TermStore";
import registrationDateStore from '@uw-it-sis/lib-react-myplan/lib/RegistrationDateStore';
import notifyStore from '@uw-it-sis/lib-react-myplan/lib/NotifyStore';
import noticesStore from '@uw-it-sis/lib-react-myplan/lib/NoticesStore';
import events from '@uw-it-sis/lib-react/lib/Events';
import PlanEvents from '@uw-it-sis/lib-react-myplan/lib/PlanEvents';
import NotifyEvents from '@uw-it-sis/lib-react-myplan/lib/NotifyEvents';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import {isFalse, isEmpty, defaultIfEmpty, capitalize, isNow} from '@uw-it-sis/lib-react/lib/Utils';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import {buildRegistrationSlnList} from '@uw-it-sis/lib-react-myplan/lib/RegistrationUtils';
import {RegEx} from "@uw-it-sis/lib-react/lib/ValidationUtils";

import {getSectionLegendFlags} from "../common/Utils";
import api from "../lib/Api";
import {MetricLabels} from "@uw-it-sis/lib-react-myplan/lib/Constants";
import RegToolsInjector from '../common/RegToolsInjector';
import {NextAction, PlanItemType} from "../lib/Constants";
import NowDateTimeEvents from "@uw-it-sis/lib-react/lib/NowDateTimeEvents";

const analyticsStore = appStore.getAnalyticsStore();
const urlPersistenceStore = appStore.getUrlPersistenceStore();
const exceptionUtils = appStore.getExceptionUtils();
class RegistrationDetailsStore {
    state = States.initial;

    data = {};
    termData = {};
    // populated by _loadRegistration
    registrationData = {};
    // populated by _loadData
    planData = {};
    plannedActivities = {};

    showFailureMessageModal = false;

    constructor() {
        makeObservable(this, {
            state: observable,
            data: observable,
            showFailureMessageModal: observable,
            mount: action,
            updateRegistrationItems: action,
            clearFailureMessage: action,
            checkDataLoadStates: action
        });

        this.componentKey = "RegistrationDetails";
        this.failureTitle = "";
        this.failureMessage = "";
        this.planData = {};

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            termStoreState: States.initial,
            registrationDetails: States.initial,
            registrationData: States.initial,
            notify: States.initial
        };

        // Register the store for URL persistence.
        urlPersistenceStore.register(this.componentKey, this.defaultData);
    }

    mount(termId) {
        // Look for data in URL persistence.
        this.data = defaultIfEmpty(urlPersistenceStore.getPersistedData(this.componentKey), this.defaultData());

        // If the term from the url persistence does not match the term mounted ( which should have come from the url ... )
        // log a warning, since something is probably not right
        if (!isEmpty(this.data.termId) && this.data.termId !== termId) {
            console.warn(`RegistrationDetailsStore: mount term id passed in to mount ( ${termId} ) does not match term id found in url persistence ( ${this.data.termId} )`);
        }

        this.notifyStoreDisposer = observe(notifyStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.notify = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        this.termStoreDisposer = observe(termStore, "state", function (change) {
            // Set the data state for this store to the new value.
            this.dataLoadStates.termStoreState = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        }.bind(this));

        termStore.load('publishedTerms');

        events.listen(PlanEvents.planItemTypeChanged, () => this.refreshData(true));
        events.listen(PlanEvents.planItemAdded, () => this.refreshData(true));
        events.listen(PlanEvents.planItemUpdated, () => this.refreshData(true));
        events.listen(PlanEvents.planItemRemoved, () => this.refreshData(true));
        events.listen(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));
        events.listen(NotifyEvents.deleted, () => this.refreshData(false));
        events.listen(NotifyEvents.added, ()  => this.refreshData(false));
        // re-mount when a new start time is set
        events.listen(NowDateTimeEvents.setNow, (newStartTime) => this.refreshData(false))
        this.failureMessage = "";
        this.failureTitle = "";

        this._loadData(termId, false);
    }

    unmount() {
        this.notifyStoreDisposer();

        events.remove(PlanEvents.planItemTypeChanged, () => this.refreshData(true));
        events.remove(PlanEvents.planItemAdded, () => this.refreshData(true));
        events.remove(PlanEvents.planItemUpdated, () => this.refreshData(true));
        events.remove(PlanEvents.planItemRemoved, () => this.refreshData(true));
        events.remove(PlanEvents.planItemError, (eo) => this.onPlanItemError(eo));
        events.remove(NotifyEvents.deleted, () => this.refreshData(false));
        events.remove(NotifyEvents.added, ()  => this.refreshData(false));
        events.remove(NowDateTimeEvents.setNow, (newStartTime) => this.refreshData(true));
    }

    defaultData() {
        return ({
            termId: "",
            registrationItems: [],
        });
    }

    /**
     * Loads the data from the planned term service which gives us the academic record and planned courses (among other data)
     * @param termId - the atpId of the term in which we want to fetch data
     * @param isRegDetailsOnly - Only load the registration details data.
     */
    _loadData = flow(function* (termId, isRegDetailsOnly) {
        this.dataLoadStates.registrationDetails = States.pending;

        this.data.termId = termId;

        if (isRegDetailsOnly) {
            this.dataLoadStates.notify = States.done;
        } else {
            // Reset the data load states object.
            this.dataLoadStates.notify =  States.pending
            this._loadNotifyData();
        }

        if ( appStore.getAppAttribute("enable_new_reg_exp") ) {
            // Get the term data for the selected term id (via component prop)
            let publishedTerms = termStore.get('publishedTerms');

            this.termData = publishedTerms.find(function (term) {
                return term.id === termId;
            }.bind(this));

            // if before reg services start, return reg services not open messaging
            if (isNow("before", "RegistrationServicesStart", appStore, this.termData)) {
                // set the next action to the reg services not open
                this.registrationData = {
                    nextAction: NextAction.REG_SVCS_NOT_OPEN
                }
               // this.dataLoadStates.internalProcessing = States.done;
                this.dataLoadStates.registrationData = States.done;
                this.checkDataLoadStates();
            } else if ( isNow("after", "RegistrationServicesStart", appStore, this.termData) && isNow("before", "FirstRegistrationPeriod", appStore, this.termData)) {
                // within the reg services start and general P1 start, make call to prereg check to determine next action
                this.checkPreReg();
            } else if ( isFalse( isNow("before", "FirstRegistrationPeriod", appStore, this.termData) ) ) {
                // Now is after  P1 start date, reg api service will now determine next action
                this._loadRegistration(termId);
            }
        } else {
            this.dataLoadStates.registrationData = States.done;
        }

        this.checkDataLoadStates();



        let terms = [];
        try {
            terms = yield api.getPlanTerms(termId, true);
            this.planData = terms[0];
            this._buildRegistrationItems(this.getFilteredPlannedList(false));

            this._doDataLoadAnalytics(termId);

            // Write data to persistence.
            urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);

            if (appStore.isDevMode()) {
                console.log(`RegistrationDetailsStore: Data load complete for term ${termId}`);
            }

            this.dataLoadStates.registrationDetails = States.done;
        } catch(error) {
            exceptionUtils.handleException(error, "RegistrationDetailsStore.loadData");
            this.dataLoadStates.registrationDetails = States.error;
        }

        this.checkDataLoadStates();
    }.bind(this));

    /**
     * (Re)load notify data.
     * @private
     */
    _loadNotifyData() {
        if (! appStore.isAdviser() && appStore.isAuthenticated() && ! appStore.isSocialUser()) {
            if (appStore.isDebug()) {
                console.log(`CourseDetailsStore[${this.courseCode}]: Reloading notify data.`);
            }
            // Call for the reload.
            notifyStore.loadNotifyData(true);
        } else {
            // Otherwise, just resolve the request.
            this.dataLoadStates.notify = States.done;
        }
    }

    /**
     * Forces a reload of the plan items from the service.  This method should really only be used
     * when a mock date is set and a different term should be viewed than what would usually load.
     *
     * This method clears the success, failure, and action pending flags, then calls loadData
     *
     * @param termId
     */
    forceLoadNewTerm(termId) {
        this.mount(termId);
    }

    /**
     * Reloads the data from the planned term service.
     */
    refreshData(isRegDetailsOnly) {
        this._loadData(this.data.termId, isRegDetailsOnly);
    }

    /**
     * Returns true if the data is available. Otherwise, false.
     */
    hasData() {
        return isFalse(isEmpty(this.planData));
    }

    /**
     * Puts together analytics around the state of the data after a data load.
     * @param courses
     * @private
     */
    _doDataLoadAnalytics(termId) {
        const dataStateSummary = this.getCurrentDataStateSummary();

        let additionalData = {
            regdCnt: dataStateSummary.registeredCount,
            cIssuesCnt: dataStateSummary.courseIssuesCount,
            sIssuesCnt: dataStateSummary.sectionIssuesCount,
            readyCnt: dataStateSummary.readyCount,
            holdsCnt: dataStateSummary.holdsCount,
            regDate: dataStateSummary.registrationDate
        }
        const labels = analyticsStore.label(MetricLabels.quarter,termId).get();
        analyticsStore.addEvent("RegPrev-DataLoad", labels, additionalData);

    }

    /**
     * Get a data structure with the current counts.
     *
     *
     */
    getCurrentDataStateSummary() {
        let dataStateSummary = {
            registeredCount: 0,
            courseIssuesCount: 0,
            sectionIssuesCount: 0,
            readyCount: 0,
            holdsCount: 0,
            registrationDate: ""
        };

        dataStateSummary.registeredCount = this.getAcademicRecord().length;
        dataStateSummary.registrationDate = this.getRegistrationDate();
        dataStateSummary.readyCount =  0;
        dataStateSummary.courseIssuesCount = 0;
        dataStateSummary.sectionIssuesCount = 0;
        dataStateSummary.holdsCount = this.getNoticesCount("REG_HOLD");

        this.planData.plannedList.forEach(function(course) {
            let thisCourseIssuesCount = 0;
            let thisSectionIssuesCount = 0;
            if (course.validationIssues.length > 0) {
                thisCourseIssuesCount++;
            }
            course.planActivities.forEach(function(section) {
                if (section.validationIssues.length > 0) {
                    thisSectionIssuesCount++;
                }
            });

            if (thisCourseIssuesCount === 0 && thisSectionIssuesCount === 0) {
                dataStateSummary.readyCount++;
            } else {
                dataStateSummary.courseIssuesCount = dataStateSummary.courseIssuesCount + thisCourseIssuesCount;
                dataStateSummary.sectionIssuesCount = dataStateSummary.sectionIssuesCount + thisSectionIssuesCount;
            }

        });

        return dataStateSummary;
    }

    /**
     * Gets the users date in YYYY-MM-DD format.
     *
     * NOTE: This code is mostly duplicated over in RegistrationButton. Also, an assumption is being made that the term
     * data has already been loaded. That's probably true, but ???
     *
     * @return {*} The formatted date if it exists or "Undefined".
     */
    getRegistrationDate() {
        // Get the term data for the selected term id (via component prop)
        let publishedTerms = termStore.get('publishedTerms');

        let selectedTerm = publishedTerms.find(function (term) {
            return term.id === this.data.termId;
        }.bind(this));

        // If the notices data isn't available or the
        let date = "Undefined";
        if (registrationDateStore.state === States.done) {
           let momentDate = registrationDateStore.getRegistrationDateForTerm(selectedTerm.codeName);
           if (momentDate) {
               date = momentDate.format("YYYY-MM-DD");
           }
        }
        return date;
    }

    hasReady() {
        return ( this.getFilteredPlannedList( false ).length > 0 );
    }

    readyCount() {
        return this.getFilteredPlannedList( false ).length;
    }

    hasIssues() {
        return ( this.getFilteredPlannedList( true ).length > 0 );
    }

    issuesCount() {
        return this.getFilteredPlannedList( true ).length;
    }

    isEmpty(listType) {
        switch (listType) {
            case PlanItemType.backup:
                return ( this.getFullBackupList().length === 0 );
            case PlanItemType.planned:
            default:
                return ( this.getFullPlannedList().length === 0 );
        }

    }

    /**
     * Gets the list of academic record items
     * @return {array} of academic record items
     */
    getAcademicRecord() {
        return this.planData.academicRecord;
    }

    /**
     * Gets the list of planned courses minus placeholder items.
     * @return {array} of planned courses
     */
    getFullPlannedList() {
        // Filter out placeholder courses (e.g. COM 2xx)
        return this.planData.plannedList.filter(course => isFalse(course.placeHolder));
    }

    /**
     * Gets the list of backup courses minus placeholder items.
     * @return {array} of planned courses
     */
    getFullBackupList() {
        // Filter out placeholder courses (e.g. COM 2xx)
        return this.planData.backupList.filter(course => isFalse(course.placeHolder));
    }

    /**
     * Gets a list of planned items that are either ready to send to registration or not ready for registration
     *
     * @param hasIssues - A boolean dictating whether the method should return the list of courses ready or ones that have issues.
     * @return {array} of courses that are either ready or not ready to register, based on param boolean when making the call.
     */
    getFilteredPlannedList( hasIssues ) {
        // if the param is not supplied, we will set it to false for a comparison later
        if ( hasIssues === undefined ) {
            hasIssues = false;
        }

        // iterate over the list of planned courses to see if the course or activities have or do not have issues (based on function param)
        let plannedList = this.getFullPlannedList().filter(course => {
            let hasValidationIssue = false;

            if ( course.validationIssues && !isEmpty(course.validationIssues) ) {
                // in this case, the course has issues, change flag and proceed
                hasValidationIssue = true;
            } else {
                // check to see if any activities has issues or is part of a block reg that has issues
                hasValidationIssue = course.planActivities.some(activity => (activity.validationIssues && !isEmpty(activity.validationIssues)) || activity.partOfBlockWithIssues );
            }
            // return if desired list to return matches whether or not it has issues
            return ( hasIssues === hasValidationIssue );
        });

        return plannedList;
    }

    getLoadedTerm() {
        return this.data.termId;
    }

    /**
     * Updates the plan data properties for 'sendToRegistration' and 'entryCode'
     *
     * @param property - name of the property being updated
     * @param identifier - either a plan item id of course or an activity id of a section
     * @param value - the value to update in the data, for courses its a boolean of whether
     * or not to send to SPS, for sections a string of an add code to send to SPS
     */
    updateRegistrationItems(property, identifier, value) {
        if ( property === 'sendToRegistration' ) {
            this.data.registrationItems.map(function (course, i) {
                if ( course.planItemId === identifier ) {
                    analyticsStore.addEvent("RegPrev-CourseSelection",{}, {
                        cCode: course.courseCode,
                        selected: `${value}`
                    });
                    this.data.registrationItems[i][property] = value;
                }
            }.bind(this));
        }

        if ( property === 'entryCode' ) {
            this.data.registrationItems.map(function (course, n) {
                course.planActivities.map(function (activity, i) {
                    if ( activity.activityId === identifier ) {
                        this.data.registrationItems[n].planActivities[i][property] = value;
                    }
                }.bind(this));
            }.bind(this));
        }

        // Write data to persistence.
        urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);
    }

    /**
     * Gets the value of either the 'sendToRegistration' or 'entryCode' properties from the
     * registration items in the state.
     *
     * @param property - name of the property for which the value is being returned
     * @param identifier - either a plan item id of course or an activity id of a section
     * @return the value of the either the 'sendToRegistration' or 'entryCode' of the course or section
     */
    getRegistrationItemValue(property, identifier) {
        let returnValue = null;

        if ( property === 'sendToRegistration' ) {
            this.data.registrationItems.map(function (course, courseIndex) {
                // If the identifier passed is an SLN, we must dig into the activities to figure out the value.
                // Typically used in the case of a block reg, we pass SLN of the block reg parent
                if ( RegEx.sln.test(identifier) ) {
                    course.planActivities.map(function (activity) {
                        if ( activity.registrationCode === identifier ) {
                            returnValue = this.data.registrationItems[courseIndex][property];
                        }
                    }.bind(this));
                } else {
                    if ( course.planItemId === identifier ) {
                        returnValue = this.data.registrationItems[courseIndex][property];
                    }
                }
            }.bind(this));
        }

        if ( property === 'entryCode' ) {
            this.data.registrationItems.map(function (course, courseIndex) {
                course.planActivities.map(function (activity, activityIndex) {
                    if ( activity.activityId === identifier ) {
                        returnValue = this.data.registrationItems[courseIndex].planActivities[activityIndex][property];
                    }
                }.bind(this));
            }.bind(this));
        }

        return returnValue;
    }

    /**
     * Builds a lightweight list of items that can be sent to SPS with properties that store form data.
     * This method is called after plan data has successful loaded.
     * The parent course has a boolean for whether or not to send that course to SPS.
     * Each activity (section) has a string for holding an entry code to send to SPS.
     *
     * @param data - response from the planned term api call
     * @return object of planned term data with new properties
     * @private
     */
    _buildRegistrationItems(data) {
        let registrationItems = [];
        // Get a list of SLN (excluding children of blocks that have parent planned)
        let slnList = buildRegistrationSlnList(data);

        data.map(function(course) {
            let sendToRegistration = this.getRegistrationItemValue('sendToRegistration', course.planItemDataObject.id);

            let tempCourse = {
                courseCode: course.courseDetails.code.replace(/\s\s+/g, ' '),
                planItemId: course.planItemDataObject.id,
                sendToRegistration: (sendToRegistration === null ? true : sendToRegistration),
                planActivities: []
            };

            course.planActivities.map(function (activity) {
                let entryCode = this.getRegistrationItemValue('entryCode', activity.activityId);

                let tempActivity = {
                    activityId: activity.activityId,
                    registrationCode: activity.registrationCode,
                    blockRegistration: activity.blockRegistration,
                    entryCode: (entryCode === null ? '' : entryCode)
                };
                tempCourse.planActivities.push(tempActivity);

                this.plannedActivities[activity.registrationCode] = {
                    cCode: tempCourse.courseCode,
                    sCode: activity.code,
                    sln: activity.registrationCode
                }
            }.bind(this));

            // Determine if any of the sections of this course are in the SLN list
            let hasActivitiesInSlnList = slnList.some(sln => course.planActivities.map(a => a.registrationCode).indexOf(sln) >= 0);
            // If no sections of the course are in the SLN list, set the send to reg prop to false (means the course is a block child)
            if ( !hasActivitiesInSlnList ) {
                tempCourse.sendToRegistration = false;
            }

            registrationItems.push(tempCourse);
        }.bind(this));

        this.data.registrationItems = registrationItems;
    }

    /**
     * Return the set of mapped registration items, including add code and flag on whether to send the course to registration
     *
     * @returns {Array|*}
     */
    getRegistrationItems() {
        return this.data.registrationItems;
    }

    /**
     * Returns a course item associated with an SLN (course and section code). Used for analytics
     *
     * @param sln
     * @returns {object|null}
     */
    getRegistrationItemBySln = (sln) => {
        if (this.plannedActivities.hasOwnProperty(sln)) {
            return this.plannedActivities[sln];
        } else {
            return null;
        }
    }

    /**
     * @return {number} The current number of selected reg items.
     */
    getSelectedRegistrationItemsCount() {
        let count = 0;
        this.data.registrationItems.forEach(function(item){
            if (item.sendToRegistration) {
                count++;
            }
        });
        return count;
    }

    getCourseContainers(courses) {
        // Get a list of SLNs from the list of courses (minus children of the block)
        let slnKeys = buildRegistrationSlnList(courses);

        // Map for container of courses
        let courseContainers = {};

        // Loop through the list of courses
        for ( let [index, course] of courses.entries() ) {
            let plannedCluster = false;
            let clusterType = undefined;
            let courseContainerKey = index;
            let activityClusters = [];
            if ( course.planActivities.length > 0 ) {
                // The key of the container map is either the primary section of the course, or the parent of the block.
                // Defaulting to primary at this point if the SLN is not 00000 (which can happen with incomplete section date, shouldn't happen on prod)
                if (course.planActivities[0].registrationCode !== "00000") {
                    courseContainerKey = course.planActivities[0].registrationCode;
                }

                // Filter the activities to only those that have block reg data associated with the section
                activityClusters = course.planActivities.filter( activity => !isEmpty(activity.blockRegistration) );
                // Iterate over sections that have block reg data, and check to see if parent of the block is within the list of SLNs
                for ( let activity of activityClusters ) {
                    plannedCluster = slnKeys.some(sln => activity.blockRegistration.parentSlns.indexOf(sln) >= 0);
                    // If block is planned, set the container key to be the parent sln of the block, set the block type, and break out of the loop
                    if (plannedCluster) {
                        courseContainerKey = slnKeys.find(sln => activity.blockRegistration.parentSlns.indexOf(sln) >= 0);
                        clusterType = activity.blockRegistration.type;
                        break;
                    }
                }
            }

            // Check if container already has key, if so, then course needs to be added to the list of courses in the map's key
            if ( courseContainers.hasOwnProperty(courseContainerKey) ) {
                courseContainers[courseContainerKey].courses.push(course);
            } else {
                // Add object to the container and set clustered flag based if the the block is planned
                let isClustered = ( activityClusters.length > 0 && plannedCluster );
                courseContainers[courseContainerKey] = {
                    "clustered": isClustered,
                    "clusterType": clusterType,
                    "clusterParent": ( isClustered ? courseContainerKey.toString() : undefined ),
                    "courses": [course]
                }
            }
        }

        return courseContainers;
    }

    /**
     * Pass-thru to NoticesStore
     */
    getNoticesCount(noticeCategory) {
        return noticesStore.getNoticesCount(noticeCategory);
    }

    onPlanItemError(eventObject) {
        if ( PlanEvents[eventObject.eventType] === PlanEvents.planItemTypeChanged ) {
            this.failureTitle = `Mark ${eventObject.code} as ${capitalize(eventObject.newType)}`;
            this.failureMessage = eventObject.errMessage;
            this.showFailureMessageModal = true;
        }
    }

    clearFailureMessage() {
        this.failureMessage = "";
        this.showFailureMessageModal = false;
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Check the activities within a group of planned courses to determine if the icon legend is needed
     * for the enrollment restrictions or add code required
     * @param type - a course group displaying either planned (issues and backup) or preview (ready to reg)
     * @return {object} - object of booleans of the two types of legends present, true if that icon type is present on any activities.
     */
    showLegend(type) {
        let list;

        switch (type) {
            case "planned":
                // build a combined list of courses that have issues and backups
                list = this.getFilteredPlannedList(true).concat(this.getFullBackupList());
                break;
            case "preview":
                // build a list of courses ready for reg
                list = this.getFilteredPlannedList(false);
                break;
            default:
                list = [];
        }

        let allActivities = [];

        for (let course of list) {
            allActivities = allActivities.concat(course.planActivities);
        }

        return getSectionLegendFlags(allActivities);
    }

    /**
     * Makes service call during reg services open and P1 start window to determine if prereg is done and sets NextAction based on result
     * @type {*}
     */
    checkPreReg = flow(function*() {
        if (appStore.isDevMode()) {
            console.log(`[RegistrationDetailsStore] ${TermUtils.makeTermDescriptionFromTermId(this.data.termId)}: start prereg status check`);
        }

        try {
            // check the prereg state
            let response = yield api.getPreregStatus(this.data.termId);

            if (appStore.isDevMode()) {
                console.log(`[RegistrationDetailsStore] ${TermUtils.makeTermDescriptionFromTermId(this.data.termId)}: completed prereg status check`);
            }
            // if prereg is complete, either set to "not user's time" or let the reg api determine state
            if (response.hasOwnProperty("preregComplete") && response.preregComplete) {
                // prereg is complete
                if (isNow("before", "FirstRegistrationPeriod", appStore,  this.termData) ) {
                    // prereg is complete, but general P1 has not started, return a standard response that registration is not open
                    this.registrationData = {
                        nextAction: NextAction.STUDENT_REG_NOT_OPEN
                    }
                    this.dataLoadStates.registrationData = States.done;
                } else {
                    // prereg is complete, and P1 has started, let reg api determine next action
                    this._loadRegistration();
                }
            } else {
                // prereg is not complete, set NextAction
                this.registrationData = {
                    nextAction: NextAction.PREREG
                }
                this.dataLoadStates.registrationData = States.done;
            }
        } catch (error) {
            exceptionUtils.handleException(error, `RegistrationDetailsStore.checkPreReg`);
            this.dataLoadStates.registrationData = States.error;
        } finally {
            this.checkDataLoadStates();
        }
    }.bind(this));

    _loadRegistration = flow(function* (termId) {
        this.dataLoadStates.registrationData = States.pending;
        this.checkDataLoadStates();

        // if no termId is passed in, use the default of the loaded term
        if (!termId) {
            termId = this.data.termId;
        }

        try {
            if (RegToolsInjector.isPreProdEnvironment()) {
                this.registrationData = yield RegToolsInjector.forgeRegistrationCall(termId);
            } else {
                this.registrationData = yield api.getRegistration(termId);
            }

            if (appStore.isDevMode()) {
                console.log(`RegistrationDetailsStore: Registration load complete for term ${termId}`);
            }
            this.dataLoadStates.registrationData = States.done;
        } catch (e) {
            exceptionUtils.handleException(e, "RegistrationDetailsStore.loadRegistration");
            // Set state to done to allow UI to render a different state
            this.dataLoadStates.registrationData = States.done;
        }
        this.checkDataLoadStates();
    }.bind(this))
}

export default new RegistrationDetailsStore();

